import { useEffect, useRef, useState } from "react";

import { useNotes } from "../../api/hooks/useNotes";
import { useUpsertTradeNote } from "../../api/hooks/useUpsertTradeNote";
import { cn } from "../../lib/cn";
import { Textarea } from "../ui/textarea";

// Time the editor waits after the last keystroke before autosaving (ADR 0036 D3).
const AUTOSAVE_DEBOUNCE_MS = 800;

type SaveStatus = "idle" | "saving" | "saved" | "error";

// The single-note autosave notepad for a trade (ADR 0036 D3/D4/D8). It binds to
// the trade's OLDEST live note -- the same row the backend `UpsertTradeNote`
// writes (it sorts `created_at ASC`), so the displayed note always equals the
// autosave target even when a U4-era trade still holds several live notes. The
// query is pinned to `created_at`/`asc` because the list endpoint defaults to
// `updated_at`/`desc`, which would otherwise surface a different first row.
//
// There is no Save button: edits debounce, and a blank (whitespace-only) body
// soft-deletes the note (the route returns `null`). The pending save is flushed
// on blur and on unmount so closing the preview panel mid-debounce never drops
// the last keystrokes. A background refetch never clobbers the draft -- the
// editor re-seeds only when the bound note id changes.
export function TradeNoteEditor({ tradeId }: { tradeId: string }) {
  const notesQuery = useNotes({
    note_type: "trade",
    trade_id: tradeId,
    sort_key: "created_at",
    sort_order: "asc",
  });
  const target = notesQuery.data?.items[0];
  const upsert = useUpsertTradeNote(tradeId);

  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState<SaveStatus>("idle");

  // The note id the draft is currently seeded from. We re-seed only when this
  // changes (initial load, create, delete) so a refetch can't overwrite typing.
  const boundIdRef = useRef<string | null>(null);
  const savedValueRef = useRef("");
  const draftRef = useRef("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `mutate` captured in a ref so the unmount flush can fire the final save
  // without re-subscribing the cleanup effect on every render.
  const mutateRef = useRef(upsert.mutate);
  mutateRef.current = upsert.mutate;

  const targetId = target?.note_id ?? null;
  const targetBody = target?.body ?? "";

  useEffect(() => {
    if (notesQuery.isLoading) return;
    if (boundIdRef.current === targetId) return;
    boundIdRef.current = targetId;
    savedValueRef.current = targetBody;
    draftRef.current = targetBody;
    setDraft(targetBody);
    setStatus("idle");
  }, [targetId, targetBody, notesQuery.isLoading]);

  // Flush any pending debounce on unmount with a final save (no state writes --
  // the component is gone). The hook's own onSuccess (cache invalidation) runs.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      if (draftRef.current !== savedValueRef.current) {
        mutateRef.current(draftRef.current);
        savedValueRef.current = draftRef.current;
      }
    };
  }, []);

  const save = (value: string) => {
    if (value === savedValueRef.current) return;
    savedValueRef.current = value;
    setStatus("saving");
    upsert.mutate(value, {
      onSuccess: (note) => {
        boundIdRef.current = note?.note_id ?? null;
        setStatus("saved");
      },
      onError: () => setStatus("error"),
    });
  };

  const handleChange = (value: string) => {
    setDraft(value);
    draftRef.current = value;
    setStatus("idle");
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      save(value);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const flush = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    save(draftRef.current);
  };

  const statusLabel =
    status === "saving"
      ? "Saving…"
      : status === "saved"
        ? "Saved"
        : status === "error"
          ? "Couldn't save — keep typing to retry"
          : "";

  return (
    <div
      data-testid="trade-note-editor"
      className="rounded-[11px] border border-outline-variant bg-surface-container p-3"
    >
      <Textarea
        aria-label="Trade note"
        data-testid="trade-note-editor-input"
        value={draft}
        onChange={(event) => handleChange(event.target.value)}
        onBlur={flush}
        placeholder="Write a note about this trade… (autosaves; clearing it deletes the note)"
        rows={4}
        disabled={notesQuery.isLoading}
      />
      <div className="mt-2 flex items-center justify-between text-[10.5px]">
        <span className="text-on-surface-muted">Autosaves to this trade&apos;s journal.</span>
        <span
          role="status"
          aria-live="polite"
          data-testid="trade-note-status"
          className={cn(status === "error" ? "text-danger" : "text-on-surface-muted")}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  );
}
